<script setup lang="ts">
  import { computed, watch } from 'vue';
  import { VCard } from 'vuetify/components/VCard';
  import { VDialog } from 'vuetify/components/VDialog';

  import { BvBtn } from '@bv/components';
  import CloseIcon from '@bv/components/icons/CloseIcon.vue';
  import { trans } from '@bv/utils';
  import { toSystemColor } from '@bv/utils/common/color/to-system-color';

  import { provideBvModal } from './composables/modal-context';
  import { useModalQueryName } from './composables/modal-query-name';

  defineOptions({
    inheritAttrs: false,
  });

  interface Props {
    title?: string;
    subtitle?: string;
    contentTitle?: string;
    content?: string;
    maxWidth?: string | number;
    persistent?: boolean;
    eager?: boolean;
    location?: 'end';
    hideHeader?: boolean;
    hideFooter?: boolean;
    hideCloseBtn?: boolean;
    hideConfirm?: boolean;
    hideCancel?: boolean;
    disableConfirm?: boolean;
    disableCancel?: boolean;
    loadingConfirm?: boolean;
    loadingCancel?: boolean;
    confirmText?: string;
    cancelText?: string;
    confirmColor?: string;
    cancelColor?: string;
    /**
     * General modal name. When set with URL query sync, open state is written as
     * `?<urlQueryKey>=<name>` (default key `modal`). May be reused by other features.
     * Omit for a plain local modal (Storybook, non-routed embeds).
     */
    name?: string;
    /**
     * The URL query key to synchronize when {@link name} is set and URL sync is on.
     * @default 'modal'
     */
    urlQueryKey?: string;
    /**
     * Whether this instance syncs open state with the URL query. Disable on
     * hidden tabs (and gate on policy) so sibling instances do not fight over
     * the same key.
     * @default true
     */
    urlQuerySyncEnabled?: boolean;
  }

  interface Emits {
    (e: 'after-enter'): void;
    (e: 'after-leave'): void;
    (e: 'close'): void;
    (e: 'confirm'): void;
    (e: 'cancel'): void;
  }

  const props = withDefaults(defineProps<Props>(), {
    title: '',
    subtitle: undefined,
    contentTitle: '',
    content: '',
    maxWidth: '560',
    persistent: false,
    eager: false,
    location: undefined,
    hideHeader: false,
    hideFooter: false,
    hideCloseBtn: false,
    hideConfirm: false,
    hideCancel: false,
    disableConfirm: false,
    disableCancel: false,
    loadingConfirm: false,
    loadingCancel: false,
    confirmText: () => 'global.form.confirm_trans',
    cancelText: () => 'global.form.cancel_trans',
    confirmColor: 'base',
    cancelColor: 'base',
    name: undefined,
    urlQueryKey: 'modal',
    urlQuerySyncEnabled: true,
  });

  const colors = computed(() => ({
    confirmColor: toSystemColor(props.confirmColor),
    cancelColor: toSystemColor(props.cancelColor),
  }));

  const emit = defineEmits<Emits>();

  const isOpen = defineModel<boolean>();

  useModalQueryName({
    name: () => props.name,
    urlQueryKey: () => props.urlQueryKey,
    enabled: () => props.urlQuerySyncEnabled,
    model: isOpen,
  });

  const showFooter = computed(() => {
    return !props.hideFooter && (!props.hideConfirm || !props.hideCancel);
  });

  function handleClose() {
    isOpen.value = false;
  }

  const modalContext = provideBvModal({ close: handleClose });

  const confirmState = computed(() => ({
    disabled: props.disableConfirm || modalContext.disableConfirm.value,
    loading: props.loadingConfirm || modalContext.loadingConfirm.value,
  }));

  function handleConfirm() {
    emit('confirm');
    return modalContext.confirm();
  }

  function handleCancel() {
    emit('cancel');
    isOpen.value = false;
  }

  watch(isOpen, (newVal) => {
    if (!newVal) {
      emit('close');
    }
  });
</script>

<template>
  <v-dialog
    v-model="isOpen"
    :class="$attrs.class"
    :content-class="[location === 'end' ? 'end-0 h-full' : '', $attrs['content-class']]"
    :eager
    :max-width="maxWidth"
    :persistent="persistent"
    @after-enter="emit('after-enter')"
    @after-leave="emit('after-leave')"
  >
    <v-card>
      <!-- Header -->
      <div v-if="!hideHeader">
        <slot name="header">
          <div class="flex flex-col gap-4 p-6 pb-2">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <template v-if="title || $slots.title">
                  <slot name="title">
                    <h3 class="text-xl font-semibold text-gray-900">
                      {{ trans(title) }}
                    </h3>
                    <h4
                      v-if="subtitle"
                      class="text-gray-600"
                    >
                      {{ trans(subtitle) }}
                    </h4>
                  </slot>
                </template>
              </div>

              <!-- Close Button -->
              <bv-btn
                v-if="!hideCloseBtn"
                class="h-8 w-8 p-0"
                color="var(--color-gray-900)"
                :disabled="persistent"
                variant="text"
                @click="handleClose"
              >
                <close-icon />
              </bv-btn>
            </div>

            <hr class="border-gray-200" />
          </div>
        </slot>
      </div>

      <!-- Content -->
      <div class="grow scrollbar-gutter-stable space-y-4 overflow-auto p-6 pe-5">
        <!-- Content Title -->
        <p
          v-if="contentTitle"
          class="mb-3 font-medium"
        >
          {{ trans(contentTitle) }}
        </p>
        <!-- Content content -->
        <p
          v-if="content"
          class="text-sm"
        >
          {{ trans(content) }}
        </p>

        <slot />
      </div>

      <!-- Footer -->
      <div
        v-if="showFooter"
        class="flex justify-end gap-4 p-6 pt-3"
      >
        <slot name="actions">
          <!-- Cancel Button -->
          <bv-btn
            v-if="!hideCancel"
            :color="colors.cancelColor"
            :disabled="disableCancel"
            :loading="loadingCancel"
            :text="trans(cancelText)"
            variant="text"
            @click="handleCancel"
          />

          <!-- Confirm Button -->
          <bv-btn
            v-if="!hideConfirm"
            :color="colors.confirmColor"
            :disabled="confirmState.disabled"
            :loading="confirmState.loading"
            :text="trans(confirmText)"
            variant="primary"
            @click="handleConfirm"
          />
        </slot>
      </div>
    </v-card>
  </v-dialog>
</template>
